/**
 * `PaidMediaSyncSnapchatCampaignsButton` — Task 195 (IC-7.4).
 *
 * Agency-facing manual trigger for IC-7 Snapchat campaign-level ingestion on
 * the client paid-media page. Dedicated twin of
 * `PaidMediaSyncMetaCampaignsButton` — calls the `sync-paid-media-snapchat-
 * campaigns` EF via the authenticated `syncPaidMediaSnapchatCampaigns` wrapper
 * (normal FE request flow, NEVER service-role).
 *
 * Gating mirrors the EF's server-side authority (the EF is the load-bearing
 * authority; this is UX defense-in-depth):
 *   - `canTriggerPaidMediaSync(me)` — agency-category actor holding
 *     `paid_media.account.manage` (REUSED from the Meta button; the permission
 *     is platform-agnostic, so no Meta behavior changes here).
 *   - A syncable target requires an ACTIVE, linked `snapchat_ads` ad account
 *     whose backing integration_connection is `active`. When the only linked
 *     account needs re-authorization, the control points to re-link.
 *
 * Hard rules (this component MUST NOT): use service-role; auto-sync / poll /
 * schedule (fires ONLY on user click); call Snapchat directly (the API call
 * lives in the Worker); touch OAuth / token-encryption / publishing surfaces.
 */

import { useCallback, useState } from 'react';
import { isApiError } from '../../api/client';
import { syncPaidMediaSnapchatCampaigns } from '../../api/endpoints';
import { env } from '../../env';
import { useMeContext } from '../../context/MeContextProvider';
// REUSE the platform-agnostic helpers from the Meta button (exported; the
// permission gate + the generic error copy are identical for Snapchat). The
// Meta component is NOT modified.
import { canTriggerPaidMediaSync, describeSyncError } from './PaidMediaSyncMetaCampaignsButton';
import type { AdAccountRow, MeContext, PaidMediaSyncEntityCounts } from '../../api/types';

/** Shared matcher: an ACTIVE, non-archived, linked `snapchat_ads` ad account. */
function isActiveLinkedSnapchatRow(a: AdAccountRow, clientId: string): boolean {
  return (
    a.client_id === clientId &&
    a.platform === 'snapchat_ads' &&
    a.status === 'active' &&
    a.archived_at === null &&
    typeof a.integration_connection_id === 'string' &&
    a.integration_connection_id.length > 0
  );
}

/**
 * A FE-VISIBLE status meaning the IC is unusable (`reauthorization_required` /
 * `revoked` / `expired`). A missing / undefined / null status is NOT "known
 * non-active" (the IC-2 SELECT policy exposes the embedded status only to
 * leadership / client users, so a custom-grant agency user legitimately sees
 * `null`). Mirrors the Meta button's private helper.
 */
function isKnownNonActiveIcStatus(status: AdAccountRow['integration_connection_status']): boolean {
  return typeof status === 'string' && status !== 'active';
}

/**
 * The first active, linked `snapchat_ads` ad account whose backing IC is NOT a
 * KNOWN non-active value — i.e. `active`, OR a status the caller cannot observe.
 * The EF stays the load-bearing authority (a hidden-but-broken IC surfaces via
 * the 200 `status:'failed'` path → "re-link"). Exported for tests.
 */
export function findSyncableSnapchatAdAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return (
    accounts.find(
      (a) =>
        isActiveLinkedSnapchatRow(a, clientId) &&
        !isKnownNonActiveIcStatus(a.integration_connection_status),
    ) ?? null
  );
}

/**
 * True iff an active linked `snapchat_ads` ad account exists whose backing IC
 * status is a KNOWN (FE-visible) non-active value. Drives the "re-link first"
 * pointer. Exported for tests.
 */
export function hasReauthRequiredSnapchatTarget(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some(
    (a) =>
      isActiveLinkedSnapchatRow(a, clientId) &&
      isKnownNonActiveIcStatus(a.integration_connection_status),
  );
}

/** What the Snapchat sync card should render. */
export type SnapchatSyncButtonVisibility =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'reauth' }
  | { readonly kind: 'syncable'; readonly adAccountId: string };

/**
 * Pure visibility decision for the Snapchat manual-sync card. The IC-7.4
 * build-time flag is the FIRST gate (default OFF → 'hidden'), so the merged FE
 * stays inert in production until backend activation; the remaining gates
 * mirror the EF authority (agency + paid_media.account.manage; an active linked
 * snapchat_ads account; else the re-link pointer). The flag gates ONLY this
 * sync card — NOT the connect card or connected-account visibility. Exported so
 * the flag-OFF / flag-ON behavior is unit-tested without a DOM harness.
 */
export function resolveSnapchatSyncButtonVisibility(args: {
  readonly flagEnabled: boolean;
  readonly me: MeContext | null;
  readonly accounts: ReadonlyArray<AdAccountRow>;
  readonly clientId: string;
}): SnapchatSyncButtonVisibility {
  if (!args.flagEnabled) return { kind: 'hidden' };
  if (!canTriggerPaidMediaSync(args.me)) return { kind: 'hidden' };
  const syncable = findSyncableSnapchatAdAccount(args.accounts, args.clientId);
  if (syncable !== null) return { kind: 'syncable', adAccountId: syncable.id };
  if (hasReauthRequiredSnapchatTarget(args.accounts, args.clientId)) return { kind: 'reauth' };
  return { kind: 'hidden' };
}

/**
 * Summary of a succeeded/partial Snapchat sync run, reported UP to the parent
 * (which owns the durable banner). Snapchat uses `adSquads` (not `adSets`) and
 * has NO creatives stream in phase 1.
 */
export interface PaidMediaSnapchatSyncSummary {
  readonly runStatus: 'succeeded' | 'partial';
  readonly rowsInserted: number;
  readonly rowsUpdated: number;
  readonly partial: boolean;
  readonly campaigns: PaidMediaSyncEntityCounts;
  readonly adSquads: PaidMediaSyncEntityCounts;
  readonly ads: PaidMediaSyncEntityCounts;
  readonly dailyInsights: PaidMediaSyncEntityCounts;
}

interface SnapchatSyncResponse {
  readonly status: 'succeeded' | 'partial' | 'failed';
  readonly rows_inserted: number;
  readonly rows_updated: number;
  readonly campaigns?: PaidMediaSyncEntityCounts;
  readonly ad_squads?: PaidMediaSyncEntityCounts;
  readonly ads?: PaidMediaSyncEntityCounts;
  readonly daily_insights?: PaidMediaSyncEntityCounts;
  readonly partial?: boolean;
}

/**
 * Map the EF sync response → the durable banner summary. Per-entity counts are
 * OPTIONAL on the wire (version skew), so each defaults to {0,0}. The CAMPAIGNS
 * bucket is special-cased like Meta: in a base shape the aggregate
 * rows_inserted/updated ARE the campaign counts. Exported for tests.
 */
export function toSnapchatSyncSummary(data: SnapchatSyncResponse): PaidMediaSnapchatSyncSummary {
  const counts = (c?: PaidMediaSyncEntityCounts): PaidMediaSyncEntityCounts => ({
    inserted: c?.inserted ?? 0,
    updated: c?.updated ?? 0,
  });
  return {
    runStatus: data.status === 'partial' ? 'partial' : 'succeeded',
    rowsInserted: data.rows_inserted,
    rowsUpdated: data.rows_updated,
    partial: data.partial ?? data.status === 'partial',
    campaigns: data.campaigns
      ? counts(data.campaigns)
      : { inserted: data.rows_inserted, updated: data.rows_updated },
    adSquads: counts(data.ad_squads),
    ads: counts(data.ads),
    dailyInsights: counts(data.daily_insights),
  };
}

type SyncState =
  | { readonly status: 'idle' }
  | { readonly status: 'syncing' }
  | { readonly status: 'failed'; readonly messageAr: string };

interface PaidMediaSyncSnapchatCampaignsButtonProps {
  readonly clientId: string;
  readonly accounts?: ReadonlyArray<AdAccountRow>;
  readonly onSyncSucceeded: (summary: PaidMediaSnapchatSyncSummary) => void;
}

export function PaidMediaSyncSnapchatCampaignsButton({
  clientId,
  accounts = [],
  onSyncSucceeded,
}: PaidMediaSyncSnapchatCampaignsButtonProps) {
  const { me } = useMeContext();
  const [sync, setSync] = useState<SyncState>({ status: 'idle' });

  // IC-7.4 — the build-time sync-UI flag is the FIRST gate (default OFF).
  const visibility = resolveSnapchatSyncButtonVisibility({
    flagEnabled: env.ic7SnapchatSyncUiEnabled,
    me,
    accounts,
    clientId,
  });
  const adAccountId = visibility.kind === 'syncable' ? visibility.adAccountId : null;

  const handleSync = useCallback(async () => {
    if (!adAccountId) return;
    setSync({ status: 'syncing' });
    try {
      const data = await syncPaidMediaSnapchatCampaigns({ ad_account_id: adAccountId });
      // A Worker-side failure surfaces as HTTP 200 with status:'failed' (e.g. a
      // refresh token that needs re-authorization). Render it as a failure, NOT
      // success, and do NOT refresh.
      if (data.status === 'failed') {
        setSync({ status: 'failed', messageAr: describeSyncError('sync_failed') });
        return;
      }
      onSyncSucceeded(toSnapchatSyncSummary(data));
      setSync({ status: 'idle' });
    } catch (err: unknown) {
      const code = isApiError(err) ? err.code : 'network_error';
      setSync({ status: 'failed', messageAr: describeSyncError(code) });
    }
  }, [adAccountId, onSyncSucceeded]);

  // Early returns AFTER all hooks (Rules of Hooks — constant hook count).
  // `hidden` covers flag-OFF, an unpermissioned actor, and no syncable target.
  if (visibility.kind === 'hidden') return null;

  if (visibility.kind === 'reauth') {
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>مزامنة حملات Snapchat</h2>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          الاتصال بحساب Snapchat يحتاج إعادة الربط قبل مزامنة الحملات. اطلب من العميل إعادة ربط
          الحساب، ثم أعد المحاولة.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>مزامنة حملات Snapchat</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
        اسحب أحدث حملات Snapchat لهذا الحساب الإعلاني يدويًا.
      </p>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSync}
          disabled={sync.status === 'syncing'}
        >
          {sync.status === 'syncing' ? 'جاري مزامنة الحملات…' : 'مزامنة الحملات'}
        </button>
      </div>
      {sync.status === 'failed' ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
          {sync.messageAr}
        </div>
      ) : null}
    </div>
  );
}
